import { NetworkStatus } from '@apollo/client'
import { Alert, Box, Table } from '@mui/material'
import dayjs from 'dayjs'
import { Formik } from 'formik'
import { uniqBy } from 'lodash-es'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import SectionTitle from '../../components/form/SectionTitle'
import DatatablePagination from '../../components/layout/DatatablePagination'
import { PageContent } from '../../components/layout/PageContent'
import { Section } from '../../components/layout/Section'
import FadeWrapper from '../../components/transitions/FadeWrapper'
import FormikOnChangeEffect from '../../utils/FormikOnChangeEffect'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { useCompanyDetailsContext } from '../../utils/hooks/useCompanyDetailsContext'
import {
  buildPagination,
  DEFAULT_PAGINATION,
  PaginationContextProvider,
  usePagination,
} from '../../utils/providers/paginationContext'
import {
  ActivityFeedGroupPartsFragment,
  ActivityFeedQueryVariables,
  EntityType,
  PartialUser,
  useActivityFeedQuery,
} from '../../__generated__/graphql'
import { AllRoute } from '../routeParams'
import ActivityFeedRow from './ActivityFeedRow'
import ActivityFeedSettingsMenu from './ActivityFeedSettingsMenu'
import Filter from './Filter'
import TableSkeleton from '../../components/loader/TableSkelton'
import PwsSpinner from '../../components/loader/PwsSpinner'
import { mergeActivityFeedGroupsOnAutoReload, parseActivityFeedGroup } from './activityFeedEntity'

export type ActivityFeedFilters = {
  userId: string | number
  entityType: string | EntityType
  companyId: string | number
}

const Activity: React.FC = () => {
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION })
  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        flex: 1,
      }}
    >
      <FadeWrapper>
        <PaginationContextProvider state={[pagination, setPagination]}>
          <ActivityFeed />
        </PaginationContextProvider>
      </FadeWrapper>
    </Box>
  )
}

export default Activity

const ActivityFeed: React.FC = () => {
  const t = useAppTranslations()
  const [paginationContext, setPagination] = usePagination()

  const { slug } = useParams<AllRoute>()
  const companyId = slug ? useCompanyDetailsContext().company.id : null
  const canFilterCompany = useMemo(() => !slug, [])

  const initialFilterValues = {
    userId: 'all',
    entityType: 'all',
    companyId: companyId || 'all',
  }

  const [filterValues, setFilterValues] = useState<ActivityFeedFilters>(initialFilterValues)
  const [userList, setUserIdList] = useState<Array<PartialUser>>([])
  const [autoReloadGroups, setAutoReloadGroups] = useState<Array<ActivityFeedGroupPartsFragment> | null>(null)
  const [showAllChanges, setShowAllChanges] = useState(false)
  const buildSearchQueryFromFilters = useCallback((): ActivityFeedQueryVariables => {
    return {
      userId: filterValues.userId !== 'all' ? filterValues.userId : null,
      entityType: filterValues.entityType !== 'all' ? (filterValues.entityType as EntityType) : null,
      companyId: canFilterCompany ? (filterValues.companyId !== 'all' ? filterValues.companyId : null) : companyId,
      origin: [],
    }
  }, [filterValues, canFilterCompany, companyId])

  const {
    data: activityFeedData,
    loading,
    error,
    refetch,
    networkStatus,
  } = useActivityFeedQuery({
    variables: {
      pagination: buildPagination(paginationContext),
      ...buildSearchQueryFromFilters(),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  })

  const isInitialLoading = loading && networkStatus !== NetworkStatus.refetch
  const isOnFirstPage = paginationContext.page === 0

  const queryActivityFeedGroups = useMemo(
    () => activityFeedData?.searchActivityFeedGrouped.map(parseActivityFeedGroup) ?? [],
    [activityFeedData]
  )

  useEffect(() => {
    setAutoReloadGroups(null)
  }, [filterValues, paginationContext.page, paginationContext.rowsPerPage])

  const parsedActivityFeedGroups = autoReloadGroups ?? queryActivityFeedGroups

  const handleManualRefresh = useCallback(() => {
    setAutoReloadGroups(null)
    void refetch()
  }, [refetch])

  const handleAutoReload = useCallback(async () => {
    if (!isOnFirstPage) return

    const { data } = await refetch()
    const incomingGroups = data?.searchActivityFeedGrouped.map(parseActivityFeedGroup) ?? []

    setAutoReloadGroups((previousGroups) =>
      mergeActivityFeedGroupsOnAutoReload(
        previousGroups ?? queryActivityFeedGroups,
        incomingGroups,
        paginationContext.rowsPerPage
      )
    )
  }, [isOnFirstPage, paginationContext.rowsPerPage, queryActivityFeedGroups, refetch])

  useEffect(() => {
    userList.length < 1 && getUserList()
  }, [parsedActivityFeedGroups])

  const getUserList = (): void => {
    setUserIdList(
      uniqBy(parsedActivityFeedGroups, 'user.id')
        .map((group) => group.user)
        .filter((user) => user !== null && user !== undefined) as Array<PartialUser>
    )
  }

  const activityFeedSections = useMemo(() => {
    return [
      {
        title: t('activity.todayTitle', { date: dayjs().format('DD MMMM YYYY') }),
        activityFeedGroups: parsedActivityFeedGroups.filter((group) => dayjs().isSame(group.createdAt, 'day')),
      },
      {
        title: t('activity.yesterdayTitle', { date: dayjs().subtract(1, 'day').format('DD MMMM YYYY') }),
        activityFeedGroups: parsedActivityFeedGroups.filter((group) =>
          dayjs().subtract(1, 'day').isSame(group.createdAt, 'day')
        ),
      },
      {
        title: t('activity.earlierTitle'),
        activityFeedGroups: parsedActivityFeedGroups.filter((group) =>
          dayjs().subtract(1, 'day').isAfter(group.createdAt, 'day')
        ),
      },
    ]
  }, [parsedActivityFeedGroups, t])

  const onFilterChange = useCallback((changes: { values: ActivityFeedFilters }) => {
    if (changes?.values) {
      setFilterValues({
        userId: changes.values.userId,
        entityType: changes.values.entityType,
        companyId: changes.values.companyId,
      })
      setPagination((oldPagination) => ({ ...oldPagination, page: 0 }))
    }
  }, [])

  if (error)
    return (
      <PageContent>
        <Alert severity="error">{error.message}</Alert>
      </PageContent>
    )

  return (
    <PageContent>
      <Formik initialValues={initialFilterValues} onSubmit={() => undefined}>
        {(formik) => (
          <>
            <FormikOnChangeEffect formik={formik} onChange={onFilterChange} />
            <Filter
              onReset={() => formik.resetForm()}
              onRefresh={handleManualRefresh}
              isRefetching={networkStatus === NetworkStatus.refetch}
              userList={userList}
              canFilterCompany={canFilterCompany}
              toolbarEnd={
                <ActivityFeedSettingsMenu
                  autoReloadDisabledReason={t('activity.autoReload.pageOneOnly')}
                  autoReloadEnabled={isOnFirstPage}
                  isRefetching={networkStatus === NetworkStatus.refetch}
                  onAutoReload={handleAutoReload}
                  onShowAllChangesChange={setShowAllChanges}
                  showAllChanges={showAllChanges}
                />
              }
            />
          </>
        )}
      </Formik>
      {isInitialLoading && <TableSkeleton colNumbers={5} lineNumbers={20} />}
      {activityFeedSections
        .filter((section) => section.activityFeedGroups.length > 0)
        .map((section) => (
          <ActivityFeedSection
            key={section.title}
            title={section.title}
            activityFeedGroups={section.activityFeedGroups}
            loading={isInitialLoading}
            showAllChanges={showAllChanges}
          />
        ))}
      <Table>
        <DatatablePagination rowsDisplayed={activityFeedData?.searchActivityFeedGrouped.length || 0} />
      </Table>
    </PageContent>
  )
}

const ActivityFeedSection: React.FC<{
  title: string
  activityFeedGroups: Array<ActivityFeedGroupPartsFragment>
  loading: boolean
  showAllChanges: boolean
}> = ({ title, activityFeedGroups, loading, showAllChanges }) => {
  return (
    <FadeWrapper>
      <Section>
        <SectionTitle title={title} />
        <Box
          sx={{
            mb: 3,
          }}
        >
          {loading ? (
            <PwsSpinner />
          ) : (
            activityFeedGroups.map((group, index) => (
              <ActivityFeedRow group={group} index={index} key={group.groupId} showAllChanges={showAllChanges} />
            ))
          )}
        </Box>
      </Section>
    </FadeWrapper>
  )
}
